import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import { Box, Button, Grid, LinearProgress, Typography } from "@mui/material";
import { SiteCreateIconDashboard } from "@platformx/shared/static-assets";
import { memo } from "react";
import { useStyles } from "./SiteSetup.styles";
import { SiteSetupProps } from "./SiteSetup.types";

const SiteSetup = ({
  progress,
  totalSteps,
  websiteInfo,
  checklist,
  showEmptyState = false,
  onEditWebsite,
  emptyStateConfig = {},
}: SiteSetupProps) => {
  const classes = useStyles();
  const {
    title_one = "Together, create your first",
    title_two = "amazing website.",
    buttonLabel = "Create Now",
    onCreate,
  } = emptyStateConfig;

  // Create site Rendering
  if (showEmptyState) {
    return (
      <Box>
        <Box className={classes.emptyStateCard}>
          <Box className={classes.createIconContainer}>
            <img src={SiteCreateIconDashboard} alt='Empty State' />
          </Box>

          <Box className={classes.emptyStateTextContainer}>
            <Typography className={classes.emptyStateSubheading} variant='h6semibold'>
              {title_one}
            </Typography>
            <Typography className={classes.emptyStateSubheading} variant='h6semibold'>
              {title_two}
            </Typography>
          </Box>
          <Button
            variant='primaryButton'
            onClick={onCreate}
            data-testid='dashboard-task-accept'
            className='sm'>
            {buttonLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  // Setup Checklist Rendering
  return (
    <Box>
      <Grid container>
        {/* Website Info Section */}
        <Grid item xs={12} md={4}>
          <Box className={classes.websiteInfoCard}>
            <Box className={classes.spaceContainer}>
              <Typography variant='h5semibold'>Website Info</Typography>
              <Box className={classes.badge}>
                <CircleIcon
                  className={classes.dotIcon}
                  sx={{
                    color: progress === totalSteps ? "#4CAF50" : "#D32F2F",
                  }}
                />
                <Typography variant='p4regular' className={classes.badgeText}>
                  Not Published
                </Typography>
              </Box>
            </Box>

            <Box className={classes.infoSection}>
              <Typography variant='p4regular'>Domain</Typography>
              <Typography variant='h5regular' className={classes.domainLink}>
                {websiteInfo.domain}
              </Typography>
            </Box>
            <Box className={classes.spaceContainer}>
              <Box className={classes.infoSection}>
                <Typography variant='p4regular'>Storage Type</Typography>
                <Typography variant='h5regular' className={classes.domainLink}>
                  {websiteInfo.storageType}
                </Typography>
              </Box>

              <Button
                variant='secondaryButton'
                className='sm'
                onClick={onEditWebsite}
                data-testid='dashboard-task-accept'>
                Edit
              </Button>
            </Box>
          </Box>
        </Grid>
        {/* Setup Checklist Section */}
        <Grid item xs={12} md={8}>
          <Box className={classes.setupChecklistCard}>
            <Box className={classes.progressContainer}>
              <Typography variant='h5semibold'>Set up your website</Typography>

              <Box className={classes.progressText}>
                <Typography variant='h5semibold'>
                  {progress}/{totalSteps}
                </Typography>
                <LinearProgress
                  variant='determinate'
                  value={(progress / totalSteps) * 100}
                  className={classes.progressBar}
                />
              </Box>
            </Box>

            <Box>
              {checklist.map((item) => (
                <Box key={item.id} className={classes.checklistItem}>
                  {item.completed ? (
                    <DoneIcon className={classes.checkmarkDone} />
                  ) : (
                    <CloseIcon className={classes.checkmarkTodo} />
                  )}
                  <Typography variant='h6regular' className={classes.checklistLabel}>
                    {item.label}
                  </Typography>
                  {item.actionLabel ? (
                    <Button
                      variant='secondaryButton'
                      className='sm'
                      onClick={item.onAction}
                      data-testid='dashboard-task-accept'>
                      {item.actionLabel}
                    </Button>
                  ) : (
                    <Typography variant='p3regular'>{item.completed ? "Done" : ""}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default memo(SiteSetup);
